import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminDepartments() {
    const [items, setItems] = useState([]);
    const [name, setName] = useState("");
    const [order, setOrder] = useState("0");
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editOrder, setEditOrder] = useState("0");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/departments");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!name.trim()) return;
        try {
            await api.post("/departments", { name: name.trim(), sort_order: parseInt(order) || 0 });
            setName(""); setOrder("0");
            toast.success("Department added");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add");
        }
    };
    const remove = async (d) => {
        if (!window.confirm(`Delete department "${d.name}"?`)) return;
        try {
            await api.delete(`/departments/${d.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete");
        }
    };

    const startEdit = (d) => {
        setEditingId(d.id);
        setEditName(d.name);
        setEditOrder(String(d.sort_order));
    };
    const cancelEdit = () => setEditingId(null);
    const saveEdit = async (d) => {
        try {
            await api.put(`/departments/${d.id}`, {
                name: editName.trim(),
                sort_order: parseInt(editOrder) || 0,
            });
            toast.success("Updated");
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update");
        }
    };

    return (
        <section data-testid="admin-departments-page" className="max-w-2xl">
            <h2 className="font-serif text-3xl mb-6">Departments</h2>
            <div className="bg-card border border-border/60 rounded-2xl p-5 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 items-end">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em]">Name</Label>
                        <Input
                            data-testid="new-department-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Bakery, Beverages"
                            className="mt-1 rounded-xl"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em]">Order</Label>
                        <Input
                            data-testid="new-department-order"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            className="mt-1 rounded-xl"
                        />
                    </div>
                    <Button
                        data-testid="add-department-btn"
                        onClick={add}
                        className="rounded-full bg-primary hover:bg-primary-hover h-10"
                    >
                        <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : items.length === 0 ? (
                <div data-testid="admin-departments-empty" className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    No departments yet — add your first above.
                </div>
            ) : (
                <ul className="space-y-2">
                    {items.map((d) => (
                        <li
                            key={d.id}
                            data-testid={`department-row-${d.id}`}
                            className="flex items-center justify-between gap-3 bg-card border border-border/60 rounded-2xl px-5 py-3"
                        >
                            {editingId === d.id ? (
                                <>
                                    <div className="flex-1 grid grid-cols-[1fr_100px] gap-2">
                                        <Input
                                            data-testid={`edit-department-name-${d.id}`}
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className="rounded-xl h-9"
                                        />
                                        <Input
                                            data-testid={`edit-department-order-${d.id}`}
                                            type="number"
                                            value={editOrder}
                                            onChange={(e) => setEditOrder(e.target.value)}
                                            className="rounded-xl h-9"
                                        />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => saveEdit(d)} data-testid={`save-department-${d.id}`} className="rounded-full">
                                            <Check className="h-4 w-4 text-moss" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="rounded-full">
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <p className="font-serif text-lg">{d.name}</p>
                                        <p className="text-xs text-muted-foreground">Sort order: {d.sort_order}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`edit-department-${d.id}`} className="rounded-full">
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => remove(d)} data-testid={`delete-department-${d.id}`} className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
